import React, { useState, useEffect } from 'react';
import T from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import PubPreHeader from './components/PubPreHeader';
import PubHeader from './components/PubHeader';
import PubCategories from './components/PubCategories';
import PubLineChart from './components/PubLineChart';
import PubTopApps from './components/PubTopApps';
import PubSdks from './components/PubSdks';
import PubMapChart from './components/PubMapChart';
import ErrorStateWithReload from '../../ErrorStateWithReload';

import { updatePubSalesforceStatus } from '../actions';
import {
  quickViewSettingsSelector,
  quickViewDataSelector,
  additionalParamsSelector,
  activeEntitySelector,
  permissionsSelector,
} from '../selectors';
import api from '../api';

const PubQuickview = ({ saveQVScrollPosition, onClose }) => {
  const dispatch = useDispatch();

  const { pqv } = useSelector(quickViewSettingsSelector);
  const { countries, publisher, sdks, time_series, top_apps } = useSelector(quickViewDataSelector);
  const selectedMetric = useSelector(additionalParamsSelector);
  const { uniqKey } = useSelector(activeEntitySelector);
  const permissions = useSelector(permissionsSelector);

  const [metric, setMetric] = useState(selectedMetric.mandatoryMetric || 'downloads');
  const [salesforceAccountUrl, setSalesforceAccountUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setSalesforceAccountUrl(publisher.salesforce_account_url);
  }, [publisher.salesforce_account_url]);

  const { store, ref_no } = publisher;

  const handleSalesforceButtonClick = () => {
    setIsLoading(true);
    api
      .pushPublisherToSalesforce({ store, ref_no })
      .then(({ data: { account_url } }) => {
        if (account_url) {
          setSalesforceAccountUrl(account_url);
          dispatch(updatePubSalesforceStatus(uniqKey, account_url));
        }
      })
      .catch() // TODO: ask what to do in case of error
      .then(() => setIsLoading(false));
  };

  return (
    <div className="prospects-sidebar__container">
      <PubPreHeader
        publisher={publisher}
        settings={pqv.publisher}
        salesforceAccountUrl={salesforceAccountUrl}
        isLoading={isLoading}
        onSalesforceButtonClick={handleSalesforceButtonClick}
        onClose={onClose}
      />
      <div className="prospects-sidebar__container-scroll">
        <PubHeader
          settings={pqv.publisher}
          publisher={publisher}
          salesforceAccountUrl={salesforceAccountUrl}
          isLoading={isLoading}
          onSalesforceButtonClick={handleSalesforceButtonClick}
        />
        <PubCategories settings={pqv.publisher} publisher={publisher} />
        {pqv.time_series.error ? (
          <ErrorStateWithReload />
        ) : (
          <PubLineChart
            settings={pqv.time_series}
            metric={metric}
            timeSeries={time_series}
            onTypeChange={setMetric}
          />
        )}
        {pqv.top_apps.error ? (
          <ErrorStateWithReload />
        ) : (
          <PubTopApps
            settings={pqv.top_apps}
            publisher={publisher}
            metric={metric}
            topApps={top_apps}
            saveQVScrollPosition={saveQVScrollPosition}
          />
        )}
        {permissions.allowAppLevelSdk &&
          (pqv.sdks?.error ? (
            <ErrorStateWithReload />
          ) : (
            <PubSdks settings={pqv.sdks} publisher={publisher} sdksData={sdks} />
          ))}
        {pqv.countries.error ? (
          <ErrorStateWithReload />
        ) : (
          <PubMapChart settings={pqv.countries} metric={metric} dataMap={countries} />
        )}
      </div>
    </div>
  );
};

PubQuickview.propTypes = {
  saveQVScrollPosition: T.func.isRequired,
  onClose: T.func.isRequired,
};

export default PubQuickview;
